import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  DataTable,
  DataTableColumnHeader,
  DataTableSkeleton,
  Input,
  useDataTable,
} from '@data-delivery/d3s';
import { productsQueries } from '@data-delivery/permission-ui-data-access-state';
import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { useMemo, useState } from 'react';

export interface ProductSelectGridProps {
  businessGroupId: string | undefined;
  selected: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
}

export function ProductSelectGrid({
  businessGroupId,
  selected,
  onChange,
  disabled,
}: ProductSelectGridProps) {
  const { data: allProducts = [], isLoading } = useQuery(productsQueries.list());
  const [search, setSearch] = useState('');
  const products = useMemo(() => {
    const filteredBg = businessGroupId
      ? allProducts.filter(p => p.businessGroup === businessGroupId)
      : [];
    const filteredSearch = search.trim().length
      ? filteredBg.filter(p =>
          [p.name, p.crmCode, p.crmGroup].some(val =>
            (val || '').toLowerCase().includes(search.toLowerCase()),
          ),
        )
      : filteredBg;
    const sorted = [...filteredSearch].sort((a, b) => {
      const aSel = selected.includes(a.id) ? 1 : 0;
      const bSel = selected.includes(b.id) ? 1 : 0;
      if (aSel !== bSel) {
        return bSel - aSel;
      }

      const an = (a.name || '').toLowerCase();
      const bn = (b.name || '').toLowerCase();
      return an.localeCompare(bn);
    });
    return sorted;
  }, [allProducts, businessGroupId, search, selected]);

  type Row = (typeof products)[number];
  const columns = useMemo<Array<ColumnDef<Row>>>(
    () => [
      {
        id: 'select',
        header: () => <span />,
        size: 28,
        cell({ row }) {
          const { id } = row.original;
          const checked = selected.includes(id);
          return (
            <Checkbox
              checked={checked}
              disabled={disabled}
              onCheckedChange={val => {
                if (disabled) {
                  return;
                }

                const next = val ? [...selected, id] : selected.filter(x => x !== id);
                onChange(next);
              }}
            />
          );
        },
        enableSorting: false,
      },
      {
        accessorKey: 'name',
        header: ({ column }) => <DataTableColumnHeader column={column} title='Product' />,
        cell: ({ getValue }) => <span className='font-medium'>{getValue<string>()}</span>,
      },
      {
        accessorKey: 'crmCode',
        header: ({ column }) => <DataTableColumnHeader column={column} title='CRM Code' />,
        cell: ({ getValue }) => <span>{getValue<string | undefined>() || '—'}</span>,
        enableSorting: false,
      },
      {
        accessorKey: 'crmGroup',
        header: ({ column }) => <DataTableColumnHeader column={column} title='CRM Group' />,
        cell: ({ getValue }) => <span>{getValue<string | undefined>() || '—'}</span>,
        enableSorting: false,
      },
    ],
    [selected, onChange, disabled],
  );

  const table = useDataTable<Row>({ data: products, columns, getRowId: r => r.id });

  return (
    <Card>
      <CardHeader className='pb-3'>
        <CardTitle className='text-sm font-medium'>Products ({selected.length})</CardTitle>
      </CardHeader>
      <CardContent className='flex flex-col gap-3'>
        <div className='flex gap-2'>
          <Input
            placeholder='Search products'
            value={search}
            disabled={disabled || !businessGroupId}
            onChange={e => {
              setSearch(e.target.value);
            }}
            className='max-w-xs'
          />
          <Button
            variant='outline'
            size='sm'
            disabled={disabled || selected.length === 0}
            onClick={() => {
              onChange([]);
            }}
          >
            Clear Selection
          </Button>
        </div>
        {isLoading ? (
          <DataTableSkeleton
            withPagination={false}
            rowCount={8}
            columnCount={columns.length}
            searchableColumnCount={0}
            filterableColumnCount={0}
            showViewOptions={false}
            cellWidths={['2rem', '12rem', '8rem', '8rem']}
            shrinkZero
          />
        ) : businessGroupId ? (
          <div className='rounded-md border'>
            <DataTable
              table={table}
              className='h-[340px]'
              emptyPlaceholderTitle='Products'
              emptyPlaceholderDescription='No products found for this Business Group'
            />
          </div>
        ) : (
          <div className='text-xs text-muted-foreground'>Select a Business Group first.</div>
        )}
      </CardContent>
    </Card>
  );
}

export default ProductSelectGrid;
